import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  BookOpen,
  Video,
  Search,
  ChevronRight,
  ExternalLink,
  Loader2,
  FileText,
  HelpCircle,
  Play,
  Lightbulb,
  Rocket,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';

const HelpPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('docs');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [docsRes, tutorialsRes] = await Promise.all([
        api.get('/documentation/'),
        api.get('/tutorials/')
      ]);
      
      setDocs(docsRes.data || []);
      setTutorials(tutorialsRes.data || []);
    } catch (error) {
      console.error('Error fetching help content:', error);
      toast.error('Erreur lors du chargement du contenu d\'aide');
    } finally {
      setLoading(false);
    }
  };

  const categoryConfig = {
    getting_started: { label: 'Démarrage', icon: Rocket, color: 'bg-green-100 text-green-700' },
    features: { label: 'Fonctionnalités', icon: Lightbulb, color: 'bg-blue-100 text-blue-700' },
    faq: { label: 'FAQ', icon: HelpCircle, color: 'bg-amber-100 text-amber-700' },
    api: { label: 'API', icon: FileText, color: 'bg-purple-100 text-purple-700' },
    tips: { label: 'Astuces', icon: Lightbulb, color: 'bg-pink-100 text-pink-700' },
    advanced: { label: 'Avancé', icon: FileText, color: 'bg-slate-100 text-slate-700' }
  };

  // Filter content by search query
  const filteredDocs = docs.filter(doc => 
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTutorials = tutorials.filter(tutorial =>
    tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tutorial.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group docs by category
  const docsByCategory = filteredDocs.reduce((acc, doc) => {
    const cat = doc.category || 'features';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  // Get YouTube video ID
  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? match[1] : null;
  };

  return (
    <div className="min-h-screen flex bg-[var(--color-bg)]" data-testid="help-page">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <BookOpen className="w-7 h-7 text-blue-500" />
                Centre d'aide
              </h1>
              <p className="text-slate-500">Documentation, tutoriels et guides pour utiliser MSC Manager</p>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Rechercher dans l'aide..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="help-search-input"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
                  <TabsTrigger value="docs" className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Documentation
                    {filteredDocs.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{filteredDocs.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="tutorials" className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Tutoriels
                    {filteredTutorials.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{filteredTutorials.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Documentation Tab */}
                <TabsContent value="docs">
                  {selectedDoc ? (
                    // Document Detail View
                    <Card>
                      <CardHeader className="border-b">
                        <div className="flex items-center justify-between">
                          <div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedDoc(null)}
                              className="mb-2 -ml-2"
                            >
                              <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
                              Retour
                            </Button>
                            <CardTitle className="text-xl">{selectedDoc.title}</CardTitle>
                            <CardDescription className="mt-1">
                              <Badge className={cn("text-xs", categoryConfig[selectedDoc.category]?.color)}>
                                {categoryConfig[selectedDoc.category]?.label || selectedDoc.category}
                              </Badge>
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="py-6">
                        <div className="prose prose-slate max-w-none">
                          <ReactMarkdown>{selectedDoc.content}</ReactMarkdown>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    // Documentation List
                    <div className="grid gap-6">
                      {Object.keys(docsByCategory).length === 0 ? (
                        <Card>
                          <CardContent className="py-12 text-center">
                            <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-500">Aucune documentation disponible</p>
                            {searchQuery && (
                              <p className="text-sm text-slate-400 mt-1">
                                Essayez avec d'autres termes de recherche
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ) : (
                        Object.entries(docsByCategory).map(([category, categoryDocs]) => {
                          const catConfig = categoryConfig[category] || categoryConfig.features;
                          const CatIcon = catConfig.icon;
                          
                          return (
                            <Card key={category}>
                              <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-lg">
                                  <CatIcon className="w-5 h-5" />
                                  {catConfig.label}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid gap-2">
                                  {categoryDocs.map((doc) => (
                                    <button
                                      key={doc.id}
                                      onClick={() => setSelectedDoc(doc)}
                                      className="flex items-center justify-between p-3 rounded-lg border hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-left group"
                                      data-testid={`doc-item-${doc.id}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-slate-400 group-hover:text-blue-500" />
                                        <span className="font-medium text-slate-700 group-hover:text-blue-700">
                                          {doc.title}
                                        </span>
                                      </div>
                                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                                    </button>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Tutorials Tab */}
                <TabsContent value="tutorials">
                  {filteredTutorials.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Video className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="text-slate-500">Aucun tutoriel disponible</p>
                        {searchQuery && (
                          <p className="text-sm text-slate-400 mt-1">
                            Essayez avec d'autres termes de recherche
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {filteredTutorials.map((tutorial) => {
                        const videoId = getYouTubeId(tutorial.youtube_url);
                        const catConfig = categoryConfig[tutorial.category] || categoryConfig.features;
                        
                        return (
                          <Card key={tutorial.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`tutorial-item-${tutorial.id}`}>
                            {/* Video Thumbnail */}
                            <div className="relative aspect-video bg-slate-100">
                              {videoId ? (
                                <>
                                  <img
                                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                    alt={tutorial.title}
                                    className="w-full h-full object-cover"
                                  />
                                  <a
                                    href={tutorial.youtube_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                                  >
                                    <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                      <Play className="w-7 h-7 text-white ml-1" fill="white" />
                                    </div>
                                  </a>
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Video className="w-12 h-12 text-slate-300" />
                                </div>
                              )}
                            </div>
                            
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="font-semibold text-slate-800 line-clamp-2">
                                  {tutorial.title}
                                </h3>
                                <Badge className={cn("text-xs flex-shrink-0", catConfig.color)}>
                                  {catConfig.label}
                                </Badge>
                              </div>
                              
                              {tutorial.description && (
                                <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                                  {tutorial.description}
                                </p>
                              )}
                              
                              <a
                                href={tutorial.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium"
                              >
                                Regarder sur YouTube
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}

            {/* Contact Support Card */}
            <Card className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="py-6">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-semibold text-slate-800">Besoin d'aide supplémentaire ?</h3>
                    <p className="text-sm text-slate-600">
                      Contactez notre équipe support si vous ne trouvez pas la réponse à votre question.
                    </p>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700" asChild>
                    <a href="/tickets">
                      Contacter le support
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
